import { Controller } from "@hotwired/stimulus"
import { Modal, Tooltip } from "bootstrap"
import { disposeTooltip } from "../utils/bootstrap-tooltip.util"
import { disposeModal } from "../utils/modal-dispose.util"

export default class extends Controller {
  static targets = ["content", "scrollRegion", "surface", "shell"]

  connect() {
    this.hasMounted = false
    this.isShown = false
    this.pendingMorph = null
    this.lastSurfaceRect = null
    this.replacementDelay = 70
    this.surfaceAnimation = null
    this.closeAnimation = null
    this.backdropAnimation = null
    this.isAnimatingClose = false
    this.openAnimationDuration = this.prefersReducedMotion() ? 0 : 380
    this.closeAnimationDuration = this.prefersReducedMotion() ? 0 : 360
    this.openTimer = null
    this.closeTimer = null
    this.backdropElement = null
    this.beforeStreamRender = this.beforeStreamRender.bind(this)
    this.beforeFrameRender = this.beforeFrameRender.bind(this)
    this.handleShellResize = this.handleShellResize.bind(this)
    this.handleScroll = this.handleScroll.bind(this)
    this.handleShow = this.handleShow.bind(this)
    this.handleShown = this.handleShown.bind(this)
    this.handleHide = this.handleHide.bind(this)
    this.handleHidden = this.handleHidden.bind(this)
    this.handleBeforeCache = this.handleBeforeCache.bind(this)

    document.addEventListener("turbo:before-stream-render", this.beforeStreamRender)
    document.addEventListener("turbo:before-frame-render", this.beforeFrameRender)
    document.addEventListener("turbo:before-cache", this.handleBeforeCache)
    this.element.addEventListener("show.bs.modal", this.handleShow)
    this.element.addEventListener("shown.bs.modal", this.handleShown)
    this.element.addEventListener("hide.bs.modal", this.handleHide)
    this.element.addEventListener("hidden.bs.modal", this.handleHidden)
    if (this.hasScrollRegionTarget) {
      this.scrollRegionTarget.addEventListener("scroll", this.handleScroll, { passive: true })
    }
    if (this.hasShellTarget && "ResizeObserver" in window) {
      this.resizeObserver = new ResizeObserver(this.handleShellResize)
      this.resizeObserver.observe(this.shellTarget)
    }
    if (this.hasScrollRegionTarget && "ResizeObserver" in window) {
      this.scrollRegionResizeObserver = new ResizeObserver(this.handleScroll)
      this.scrollRegionResizeObserver.observe(this.scrollRegionTarget)
    }

    const activeModals = document.querySelectorAll(".modal.show")
    this.isStacked = activeModals.length > 0

    if (!this.isStacked) {
      document.querySelectorAll(".modal-backdrop").forEach((el) => el.remove())
    }

    this.modal = new Modal(this.element)
    this.modal.show()
  }

  disconnect() {
    document.removeEventListener("turbo:before-stream-render", this.beforeStreamRender)
    document.removeEventListener("turbo:before-frame-render", this.beforeFrameRender)
    document.removeEventListener("turbo:before-cache", this.handleBeforeCache)
    this.element.removeEventListener("show.bs.modal", this.handleShow)
    this.element.removeEventListener("shown.bs.modal", this.handleShown)
    this.element.removeEventListener("hide.bs.modal", this.handleHide)
    this.element.removeEventListener("hidden.bs.modal", this.handleHidden)
    if (this.hasScrollRegionTarget) {
      this.scrollRegionTarget.removeEventListener("scroll", this.handleScroll)
    }
    this.resizeObserver?.disconnect()
    this.scrollRegionResizeObserver?.disconnect()
    this.surfaceAnimation?.cancel()
    this.closeAnimation?.cancel()
    this.backdropAnimation?.cancel()
    window.clearTimeout(this.openTimer)
    window.clearTimeout(this.closeTimer)
    disposeModal(this.modal)
  }

  contentTargetConnected(element) {
    const state = element.dataset.state || "form"

    if (this.pendingMorph) {
      this.morphTo(state)
    } else {
      this.applyState(state, this.hasMounted)
    }

    this.refreshScrollShadows()

    this.hasMounted = true
  }

  beforeStreamRender(event) {
    const stream = event.target

    if (
      !(stream instanceof Element) ||
      stream.getAttribute("target") !== "invitation_modal_content" ||
      !this.hasSurfaceTarget
    ) {
      return
    }

    this.prepareMorph(event)
  }

  beforeFrameRender(event) {
    const frame = event.target

    if (
      !(frame instanceof Element) ||
      frame.id !== "invitation_modal_content" ||
      !this.element.contains(frame) ||
      !this.hasSurfaceTarget
    ) {
      return
    }

    this.prepareMorph(event)
  }

  prepareMorph(event) {
    const rect = this.surfaceTarget.getBoundingClientRect()
    this.pendingMorph = { rect }

    if (this.prefersReducedMotion() || !this.hasContentTarget) {
      return
    }

    const originalRender = event.detail.render
    event.detail.render = (...args) => {
      this.contentTarget?.classList.add("is-exiting")

      window.setTimeout(() => {
        originalRender(...args)
      }, this.replacementDelay)
    }
  }

  morphTo(state) {
    const start = this.pendingMorph
    this.pendingMorph = null

    this.applyState(state, false)

    if (this.prefersReducedMotion()) {
      this.lastSurfaceRect = this.captureRect(this.surfaceTarget)
      this.playContentEntrance()
      return
    }

    requestAnimationFrame(() => {
      this.animateSurfaceFrom(start?.rect, {
        duration: 380,
        easing: "cubic-bezier(0.22, 1, 0.36, 1)",
        overshoot: 0.12
      })
      this.playContentEntrance()
    })
  }

  applyState(state, animateContent) {
    this.element.dataset.state = state
    this.surfaceTarget.dataset.state = state

    if (animateContent) {
      this.playContentEntrance()
    }
  }

  playContentEntrance() {
    if (!this.hasContentTarget) {
      return
    }

    this.contentTarget.classList.remove("is-exiting")
    this.contentTarget.classList.remove("is-entering")
    this.contentTarget.offsetHeight
    this.contentTarget.classList.add("is-entering")

    window.setTimeout(
      () => {
        this.contentTarget?.classList.remove("is-entering")
      },
      this.prefersReducedMotion() ? 180 : 320
    )
  }

  handleShellResize(entries) {
    const entry = entries[0]

    if (!entry || this.pendingMorph || !this.hasSurfaceTarget || !this.isShown) {
      return
    }

    const currentRect = this.captureRect(this.surfaceTarget)

    if (!this.lastSurfaceRect) {
      this.lastSurfaceRect = currentRect
      return
    }

    const widthDelta = Math.abs(currentRect.width - this.lastSurfaceRect.width)
    const heightDelta = Math.abs(currentRect.height - this.lastSurfaceRect.height)

    if (widthDelta < 2 && heightDelta < 2) {
      this.lastSurfaceRect = currentRect
      return
    }

    if (this.prefersReducedMotion()) {
      this.lastSurfaceRect = currentRect
      return
    }

    this.animateSurfaceFrom(this.lastSurfaceRect, {
      duration: heightDelta > 56 ? 360 : 280,
      easing: "cubic-bezier(0.2, 0.9, 0.24, 1)",
      overshoot: 0.1
    })
  }

  prefersReducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches
  }

  handleShow() {
    this.element.classList.remove("is-closing")
  }

  handleShown() {
    this.decorateBackdrop()
    this.element.classList.add("is-opening")
    this.isShown = true
    this.refreshScrollShadows()
    window.clearTimeout(this.openTimer)
    this.openTimer = window.setTimeout(() => {
      this.element.classList.remove("is-opening")
      if (this.hasSurfaceTarget) {
        this.lastSurfaceRect = this.captureRect(this.surfaceTarget)
      }
    }, this.openAnimationDuration)
  }

  handleHide(event) {
    if (this.isAnimatingClose) {
      return
    }

    event?.preventDefault()

    if (this.prefersReducedMotion()) {
      this.element.classList.remove("is-opening")
      this.element.classList.add("is-closing")
      this.isAnimatingClose = true
      this.finishClose()
      return
    }

    this.isAnimatingClose = true
    this.element.classList.remove("is-opening")
    this.element.classList.add("is-closing")
    this.playCloseAnimation()
  }

  handleHidden() {
    this.element.classList.remove("is-closing")
    this.element.classList.remove("is-opening")
    this.isShown = false
    this.isAnimatingClose = false
    window.clearTimeout(this.openTimer)
    window.clearTimeout(this.closeTimer)
    this.backdropElement = null
    this.lastSurfaceRect = null
    this.surfaceAnimation?.cancel()
    this.closeAnimation?.cancel()
    this.backdropAnimation?.cancel()
    this.surfaceTarget?.classList.remove("is-morphing")
    this.disposeInnerTooltips()
    this.element.remove()

    document.dispatchEvent(new CustomEvent("modalRemoved"))

    const remainingModals = document.querySelectorAll(".modal.show")
    if (remainingModals.length > 0) {
      document.body.classList.add("modal-open")
    } else {
      document.querySelectorAll(".modal-backdrop").forEach((el) => el.remove())
      document.body.classList.remove("modal-open")
      document.body.style.removeProperty("overflow")
      document.body.style.removeProperty("padding-right")
    }
  }

  finishClose() {
    const modal = this.modal
    this.modal = null
    disposeModal(modal)
    this.handleHidden()
  }

  playCloseAnimation() {
    this.closeAnimation?.cancel()
    this.backdropAnimation?.cancel()

    this.closeAnimation = this.surfaceTarget?.animate(
      [
        {
          opacity: 1,
          transform: "translate3d(0, 0, 0) scale(1, 1)"
        },
        {
          opacity: 0,
          transform: "translate3d(0, -16px, 0) scale(0.996, 0.996)"
        }
      ],
      {
        duration: this.closeAnimationDuration,
        easing: "cubic-bezier(0.32, 0.72, 0, 1)",
        fill: "both"
      }
    )

    if (this.backdropElement) {
      this.backdropAnimation = this.backdropElement.animate(
        [{ opacity: Number.parseFloat(window.getComputedStyle(this.backdropElement).opacity) || 1 }, { opacity: 0 }],
        {
          duration: this.closeAnimationDuration,
          easing: "cubic-bezier(0.32, 0.72, 0, 1)",
          fill: "both"
        }
      )
    }

    Promise.allSettled([this.closeAnimation?.finished, this.backdropAnimation?.finished].filter(Boolean)).finally(
      () => {
        this.isAnimatingClose = false
        this.finishClose()
      }
    )
  }

  handleBeforeCache() {
    document.querySelectorAll(".modal-backdrop").forEach((el) => el.remove())
    document.body.classList.remove("modal-open")
    document.body.style.removeProperty("overflow")
    document.body.style.removeProperty("padding-right")
    disposeModal(this.modal)
    this.element.remove()
  }

  handleScroll() {
    this.refreshScrollShadows()
  }

  animateSurfaceFrom(previousRect, { duration, easing, overshoot }) {
    if (!previousRect || !this.hasSurfaceTarget) {
      this.lastSurfaceRect = this.captureRect(this.surfaceTarget)
      return
    }

    const nextRect = this.captureRect(this.surfaceTarget)
    const deltaX = previousRect.left + previousRect.width / 2 - (nextRect.left + nextRect.width / 2)
    const deltaY = previousRect.top + previousRect.height / 2 - (nextRect.top + nextRect.height / 2)
    const scaleX = previousRect.width / nextRect.width
    const scaleY = previousRect.height / nextRect.height

    if (
      Math.abs(deltaX) < 0.5 &&
      Math.abs(deltaY) < 0.5 &&
      Math.abs(scaleX - 1) < 0.002 &&
      Math.abs(scaleY - 1) < 0.002
    ) {
      this.lastSurfaceRect = nextRect
      return
    }

    const settleScaleX = 1 + (scaleX - 1) * overshoot
    const settleScaleY = 1 + (scaleY - 1) * overshoot

    this.surfaceAnimation?.cancel()
    this.surfaceTarget.classList.add("is-morphing")
    const animation = this.surfaceTarget.animate(
      [
        {
          transform: `translate3d(${deltaX}px, ${deltaY}px, 0) scale(${scaleX}, ${scaleY})`
        },
        {
          transform: `translate3d(${deltaX * overshoot}px, ${deltaY * overshoot}px, 0) scale(${settleScaleX}, ${settleScaleY})`,
          offset: 0.72
        },
        {
          transform: "translate3d(0, 0, 0) scale(1, 1)"
        }
      ],
      {
        duration,
        easing,
        fill: "both"
      }
    )
    this.surfaceAnimation = animation

    animation.finished
      .catch(() => {})
      .finally(() => {
        if (this.surfaceAnimation === animation) {
          this.surfaceTarget.classList.remove("is-morphing")
          this.surfaceAnimation = null
        }
      })

    this.lastSurfaceRect = nextRect
  }

  captureRect(element) {
    const rect = element?.getBoundingClientRect()

    if (!rect) {
      return null
    }

    return {
      left: rect.left,
      top: rect.top,
      width: rect.width,
      height: rect.height
    }
  }

  decorateBackdrop() {
    const backdrop = document.querySelector(".modal-backdrop:last-of-type")

    if (!backdrop) {
      return
    }

    this.backdropElement = backdrop
    backdrop.classList.add("lc-invitation-modal-backdrop")

    if (this.isStacked) {
      const shownCount = document.querySelectorAll(".modal.show").length
      const zBase = 1060 + (shownCount - 1) * 10
      this.element.style.zIndex = String(zBase)
      backdrop.style.zIndex = String(zBase - 5)
    }

    requestAnimationFrame(() => {
      this.backdropElement?.classList.add("is-visible")
    })
  }

  disposeInnerTooltips() {
    this.element.querySelectorAll('[data-controller~="tooltip"]').forEach((el) => {
      const instance = Tooltip?.getInstance?.(el)
      disposeTooltip(instance)
    })
  }

  refreshScrollShadows() {
    if (!this.hasScrollRegionTarget) {
      this.element.dataset.scrollShadowTop = "false"
      this.element.dataset.scrollShadowBottom = "false"
      return
    }

    const { scrollTop, scrollHeight, clientHeight } = this.scrollRegionTarget
    const maxScrollTop = scrollHeight - clientHeight
    const hasOverflow = maxScrollTop > 1

    this.element.dataset.scrollShadowTop = String(hasOverflow && scrollTop > 1)
    this.element.dataset.scrollShadowBottom = String(hasOverflow && scrollTop < maxScrollTop - 1)
  }
}
